import Phaser from 'phaser';
import { ASSET_KEYS, COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { findFloor, type FloorId } from '@/data/floors';
import { finnFloorUpgrade, type FloorUpgrade } from '@/data/floorUpgrades';
import { gameState } from '@/state/store';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_WIDTH = 880;
const SHEET_HEIGHT = 1240;
const TOPP_BORDER = 4;
const INDRE_PAD = 32;

export class FloorUpgradeModal {
  private layer?: Phaser.GameObjects.Container;
  private aktivFloor?: FloorId;
  private unsubscribe?: () => void;

  constructor(private scene: Phaser.Scene) {}

  open(floorId: FloorId): void {
    this.close();
    this.aktivFloor = floorId;
    this.layer = this.scene.add.container(0, 0).setDepth(160);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    backdrop.on('pointerup', () => this.close());
    this.layer.add(backdrop);

    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.layer?.destroy(true);
    this.layer = undefined;
    this.aktivFloor = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer || !this.aktivFloor) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const floor = findFloor(this.aktivFloor);
    const upgrade = finnFloorUpgrade(this.aktivFloor);
    if (!floor) return;

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = (GAME_HEIGHT - SHEET_HEIGHT) / 2;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, SHEET_HEIGHT, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.ink, 0.15);
    this.layer.add(sheet);

    const toppBorder = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, TOPP_BORDER, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(toppBorder);

    const lukk = this.scene.add
      .text(sheetX + SHEET_WIDTH - 28, sheetY + 20, '✕', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(36),
        color: '#3a2e22',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    lukk.on('pointerover', () => lukk.setColor('#862736'));
    lukk.on('pointerout', () => lukk.setColor('#3a2e22'));
    lukk.on('pointerup', () => this.close());
    this.layer.add(lukk);

    let y = sheetY + INDRE_PAD + 16;

    const header = this.scene.add
      .text(sheetX + INDRE_PAD, y, `Oppgrader ${floor.label}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - INDRE_PAD * 2 - 60 },
      })
      .setOrigin(0, 0);
    this.layer.add(header);
    y += header.height + 12;

    const undertittel = this.scene.add
      .text(sheetX + INDRE_PAD, y, upgrade.navn, {
        fontFamily: FONTS.body,
        fontSize: fontStr(26),
        color: '#3a2e22',
        fontStyle: 'italic',
        wordWrap: { width: SHEET_WIDTH - INDRE_PAD * 2 },
      })
      .setOrigin(0, 0);
    this.layer.add(undertittel);
    y += undertittel.height + 24;

    y = this.tegnFoerEtter(sheetX, y);
    y += 28;

    const beskrivelse = this.scene.add
      .text(sheetX + INDRE_PAD, y, upgrade.beskrivelse, {
        fontFamily: FONTS.body,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: SHEET_WIDTH - INDRE_PAD * 2 },
        lineSpacing: 6,
      })
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);
    y += beskrivelse.height + 24;

    y = this.tegnEffektListe(sheetX, y, upgrade);
    y += 24;

    this.tegnKnapp(sheetX, sheetY, upgrade);
  }

  private tegnFoerEtter(sheetX: number, y: number): number {
    if (!this.layer || !this.aktivFloor) return y;

    const bildeBredde = (SHEET_WIDTH - INDRE_PAD * 2 - 24) / 2;
    const bildeHoyde = 280;
    const venstreX = sheetX + INDRE_PAD;
    const hoyreX = venstreX + bildeBredde + 24;

    this.tegnBildeBoks(venstreX, y, bildeBredde, bildeHoyde, 'Før');
    this.tegnBildeBoks(hoyreX, y, bildeBredde, bildeHoyde, 'Etter');

    return y + bildeHoyde;
  }

  private tegnBildeBoks(
    x: number,
    y: number,
    bredde: number,
    hoyde: number,
    label: string,
  ): void {
    if (!this.layer) return;
    const ramme = this.scene.add
      .rectangle(x, y, bredde, hoyde, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.5);
    this.layer.add(ramme);

    const tekstur = this.bildeTekstur();
    if (tekstur && this.scene.textures.exists(tekstur)) {
      const bilde = this.scene.add
        .image(x + bredde / 2, y + hoyde / 2, tekstur)
        .setOrigin(0.5);
      const tex = this.scene.textures.get(tekstur).getSourceImage();
      const texW = (tex as HTMLImageElement).width || 1;
      const texH = (tex as HTMLImageElement).height || 1;
      const scale = Math.min(bredde / texW, hoyde / texH);
      bilde.setScale(scale);
      this.layer.add(bilde);
    } else {
      const placeholder = this.scene.add
        .text(x + bredde / 2, y + hoyde / 2, '🏛', {
          fontFamily: FONTS.display,
          fontSize: fontStr(128),
          color: '#8b5e3c',
        })
        .setOrigin(0.5);
      this.layer.add(placeholder);
    }

    const labelBoks = this.scene.add
      .rectangle(x + 8, y + 8, 110, 42, COLORS.white, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.oakDark, 0.5);
    this.layer.add(labelBoks);

    const labelTxt = this.scene.add
      .text(x + 8 + 55, y + 8 + 21, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add(labelTxt);
  }

  private bildeTekstur(): string | undefined {
    if (this.aktivFloor === 'redaksjon') return ASSET_KEYS.envRedaksjon;
    if (this.aktivFloor === 'direksjon') return ASSET_KEYS.envDireksjon;
    return undefined;
  }

  private tegnEffektListe(sheetX: number, y: number, upgrade: FloorUpgrade): number {
    if (!this.layer) return y;
    const overskrift = this.scene.add
      .text(sheetX + INDRE_PAD, y, 'EFFEKTER', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    this.layer.add(overskrift);
    y += overskrift.height + 12;

    for (const effekt of upgrade.effekter) {
      const linje = this.scene.add
        .text(sheetX + INDRE_PAD, y, `• ${beskrivEffekt(effekt)}`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#1a1a1a',
          wordWrap: { width: SHEET_WIDTH - INDRE_PAD * 2 - 12 },
        })
        .setOrigin(0, 0);
      this.layer.add(linje);
      y += linje.height + 8;
    }
    return y;
  }

  private tegnKnapp(sheetX: number, sheetY: number, upgrade: FloorUpgrade): void {
    if (!this.layer) return;
    const knappH = 88;
    const knappW = SHEET_WIDTH - INDRE_PAD * 2;
    const knappX = sheetX + INDRE_PAD;
    const knappY = sheetY + SHEET_HEIGHT - INDRE_PAD - knappH;

    const harRaad = gameState.kroner >= upgrade.pris;
    const farge = harRaad ? COLORS.wine : 0x9a9285;

    const bg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, farge, 1)
      .setOrigin(0, 0);
    this.layer.add(bg);

    const tekst = this.scene.add
      .text(
        knappX + knappW / 2,
        knappY + knappH / 2,
        `Oppgrader (${upgrade.pris.toLocaleString('nb-NO')} kr)`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(30),
          color: '#ffffff',
          fontStyle: '700',
        },
      )
      .setOrigin(0.5);
    this.layer.add(tekst);

    if (!harRaad) {
      const hjelp = this.scene.add
        .text(
          knappX + knappW / 2,
          knappY - 16,
          `Mangler ${(upgrade.pris - gameState.kroner).toLocaleString('nb-NO')} kr`,
          {
            fontFamily: FONTS.hud,
            fontSize: fontStr(18),
            color: '#862736',
            fontStyle: '700',
          },
        )
        .setOrigin(0.5, 1);
      this.layer.add(hjelp);
      return;
    }

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0x6f1f2c));
    bg.on('pointerout', () => bg.setFillStyle(COLORS.wine));
    bg.on('pointerup', () => this.utfoerOppgradering(upgrade.floorId));
  }

  private utfoerOppgradering(id: FloorId): void {
    if (gameState.oppgraderEtasje(id)) {
      this.close();
    }
  }
}

function beskrivEffekt(effekt: FloorUpgrade['effekter'][number]): string {
  switch (effekt.type) {
    case 'manus_per_dag_bonus':
      return `+${effekt.verdi} manus i innboksen per dag`;
    case 'kampanje_pris_rabatt':
      return `${Math.round(effekt.verdi * 100)} % rabatt på alle kampanjer`;
    case 'fase_dager_rabatt':
      return `−${effekt.verdi} dag i ${faseLabel(effekt.fase)}-fasen`;
    case 'salg_bonus_global':
      return `+${Math.round(effekt.verdi * 100)} % salg på alle bøker`;
    case 'lojalitet_decay_redusert':
      return `Forfatter-lojalitet faller saktere`;
    case 'dashboard_sesong_prognose':
      return `Direksjon-dashboard viser sesong-prognose`;
    case 'omdomme_per_uke':
      return `+${effekt.verdi} omdømme hver uke`;
  }
}

function faseLabel(fase: 'redigering' | 'design' | 'trykk'): string {
  switch (fase) {
    case 'redigering':
      return 'rediger';
    case 'design':
      return 'design';
    case 'trykk':
      return 'trykk';
  }
}
